/**
 * The site's Q&A list.
 *
 * Two things share this screen because they are the same thing at different
 * stages: questions the assistant could not answer, and answers the merchant
 * wrote. Separating them into two pages would mean the list of gaps sat
 * somewhere you had to remember to visit, and the whole point is that
 * answering one is a thirty-second job you do when prompted.
 *
 * Pending comes first and is the default filter, because an unanswered
 * question is costing the store traffic right now and a published one is not
 * asking anything of anybody.
 */

import { useState } from "react";

import {
  useCreateFaqEntry,
  useDeleteFaqEntry,
  useFaqEntries,
  useFaqSummary,
  useUpdateFaqEntry,
} from "../../api/queries";
import type { FaqEntry, FaqStatus } from "../../api/types";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Textarea,
} from "../../ui/primitives";
import { useSiteContext } from "./SiteLayout";

const FILTERS: Array<{ value: FaqStatus | undefined; label: string }> = [
  { value: "pending", label: "Needs an answer" },
  { value: "published", label: "Answered" },
  { value: "dismissed", label: "Dismissed" },
  { value: undefined, label: "All" },
];

export function QuestionsPage() {
  const { orgId, siteId } = useSiteContext();
  const [filter, setFilter] = useState<FaqStatus | undefined>("pending");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<FaqEntry | null>(null);

  const entries = useFaqEntries(orgId, siteId, filter);
  const summary = useFaqSummary(orgId, siteId);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => setFilter(option.value)}
              aria-pressed={filter === option.value}
              className={
                filter === option.value
                  ? "rounded-lg bg-ink px-3 py-1.5 text-sm font-medium text-white"
                  : "rounded-lg px-3 py-1.5 text-sm text-muted hover:text-ink"
              }
            >
              {option.label}
              {option.value === "pending" && (summary.data?.pending ?? 0) > 0 && (
                <span className="ml-1.5 tabular-nums">({summary.data?.pending})</span>
              )}
            </button>
          ))}
        </div>

        <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
          Add a question
        </Button>
      </div>

      <p className="text-sm text-muted">
        When your assistant cannot answer something, the question appears here
        and we email you. Answers you write are used from the next question on.
      </p>

      {entries.error != null && <ErrorNote error={entries.error} />}

      {entries.isLoading ? (
        <Loading />
      ) : (entries.data ?? []).length === 0 ? (
        <Card>
          <Empty title={filter === "pending" ? "Nothing waiting" : "Nothing here yet"}>
            {filter === "pending"
              ? "Your assistant has answered everything it was asked. Questions it cannot answer will show up here."
              : "Add a question and answer to get started."}
          </Empty>
        </Card>
      ) : (
        <ul className="space-y-2">
          {(entries.data ?? []).map((entry) => (
            <li key={entry.id}>
              <EntryCard entry={entry} onEdit={() => setEditing(entry)} />
            </li>
          ))}
        </ul>
      )}

      <EntryDialog
        key={editing?.id ?? (adding ? "new" : "closed")}
        entry={editing}
        open={adding || editing !== null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
      />
    </div>
  );
}

function EntryCard({ entry, onEdit }: { entry: FaqEntry; onEdit: () => void }) {
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{entry.question}</span>
            <StatusBadge entry={entry} />
            {entry.times_asked > 1 && (
              // The number that decides which gap to fill first, so it is on
              // the card rather than behind a click.
              <span className="text-xs text-muted tabular-nums">
                asked {entry.times_asked} times
              </span>
            )}
          </div>

          {entry.answer ? (
            <p className="whitespace-pre-wrap text-sm text-muted">{entry.answer}</p>
          ) : (
            <p className="text-sm text-muted">No answer yet.</p>
          )}
        </div>

        <Button size="sm" variant={entry.answer ? "ghost" : "primary"} onClick={onEdit}>
          {entry.answer ? "Edit" : "Answer"}
        </Button>
      </div>
    </Card>
  );
}

function StatusBadge({ entry }: { entry: FaqEntry }) {
  if (entry.status === "dismissed") return <Badge>Dismissed</Badge>;

  if (entry.status === "published") {
    // "Published" and "the assistant can find it" are not the same instant
    // while the projected document is being indexed, and saying so avoids a
    // merchant testing the widget in that gap and concluding it is broken.
    return entry.is_live ? (
      <Badge tone="good">In use</Badge>
    ) : (
      <Badge tone="warn">Indexing</Badge>
    );
  }

  return <Badge tone="warn">Needs an answer</Badge>;
}

function EntryDialog({
  entry,
  open,
  onClose,
}: {
  entry: FaqEntry | null;
  open: boolean;
  onClose: () => void;
}) {
  const { orgId, siteId } = useSiteContext();
  const create = useCreateFaqEntry(orgId, siteId);
  const update = useUpdateFaqEntry(orgId, siteId);
  const remove = useDeleteFaqEntry(orgId, siteId);

  const [question, setQuestion] = useState(entry?.question ?? "");
  const [answer, setAnswer] = useState(entry?.answer ?? "");

  const busy = create.isPending || update.isPending || remove.isPending;
  const error = create.error ?? update.error ?? remove.error;

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    if (entry) {
      await update.mutateAsync({
        entryId: entry.id,
        patch: { question, answer },
      });
    } else {
      await create.mutateAsync({ question, answer: answer || null });
    }
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={entry ? "Answer this question" : "Add a question"}
    >
      <form className="space-y-3" onSubmit={submit}>
        {error != null && <ErrorNote error={error} />}

        <Field
          label="Question"
          id="faq-question"
          hint="In the words a visitor would use."
        >
          <Input
            id="faq-question"
            value={question}
            required
            onChange={(event) => setQuestion(event.target.value)}
          />
        </Field>

        <Field
          label="Answer"
          id="faq-answer"
          hint="Saving an answer publishes it. Leave it empty to keep the question on your list."
        >
          <Textarea
            id="faq-answer"
            rows={5}
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
          />
        </Field>

        <div className="flex flex-wrap justify-between gap-2 pt-1">
          {/* Dismissing, not deleting: deleting a captured question does not
              stop it coming back — the next visitor to ask it captures it
              again. This is the control that actually means "stop telling me
              about this". */}
          {entry && entry.status !== "dismissed" ? (
            <Button
              disabled={busy}
              onClick={async () => {
                await update.mutateAsync({
                  entryId: entry.id,
                  patch: { status: "dismissed" },
                });
                onClose();
              }}
            >
              Not worth answering
            </Button>
          ) : (
            <span />
          )}

          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy || !question.trim()}>
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
